import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
}

export default function SEOHead({
  title = 'Kimberlite Commercial Brokerage Doha Qatar | International Trade Facilitation MENA',
  description = 'Kimberlite Commercial Brokerage in Doha Qatar offers premier international trade facilitation, sourcing reliable suppliers and buyers, contract negotiation, company setup, banking services, customs clearance, and business consulting across MENA region, Asia, Europe, and Americas. Expert commercial brokerage services connecting Qatar businesses to global markets with transparency and proven expertise.',
  keywords = 'commercial brokerage Doha Qatar, international trade facilitation Qatar, MENA trade services, import export Qatar, sourcing suppliers buyers Qatar, contract negotiation Doha, company setup Qatar, banking services Qatar, customs clearance Qatar, business consulting Qatar, trade brokerage MENA',
  canonical = '/',
  ogImage,
}: SEOHeadProps) {
  const { i18n } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kimberlite.qa';

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Basic SEO
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateLinkTag('canonical', `${siteUrl}${canonical}`);

    // Open Graph
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:locale', i18n.language === 'ar' ? 'ar_QA' : 'en_US', true);
    updateMetaTag('og:locale:alternate', i18n.language === 'ar' ? 'en_US' : 'ar_QA', true);
    
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Update last modified date
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Update HTML lang and dir attributes
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

    // Add or update Schema.org JSON-LD
    const addOrUpdateSchema = (id: string, schema: object) => {
      let scriptElement = document.querySelector(`script[data-schema-id="${id}"]`);
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('data-schema-id', id);
        document.head.appendChild(scriptElement);
      }
      scriptElement.textContent = JSON.stringify(schema);
    };

    // Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Kimberlite Commercial Brokerage",
      "url": siteUrl,
      "logo": `${siteUrl}/logo.png`,
      "description": "Premier commercial brokerage services specializing in international trade facilitation across MENA, Asia, Europe, and Americas",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Doha",
        "addressRegion": "Doha",
        "addressCountry": "QA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "25.2854",
        "longitude": "51.5310"
      },
      "areaServed": [
        {
          "@type": "Place",
          "name": "MENA Region"
        },
        {
          "@type": "Place",
          "name": "Asia"
        },
        {
          "@type": "Place",
          "name": "Europe"
        },
        {
          "@type": "Place",
          "name": "Americas"
        }
      ],
      "serviceType": [
        "International Trade Facilitation",
        "Commercial Brokerage",
        "Sourcing Services",
        "Contract Negotiation",
        "Company Setup",
        "Banking Services",
        "Customs Clearance",
        "Business Consulting"
      ]
    };

    // WebSite Schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Kimberlite Commercial Brokerage",
      "url": siteUrl,
      "description": description,
      "inLanguage": [i18n.language === 'ar' ? 'ar' : 'en', i18n.language === 'ar' ? 'en' : 'ar']
    };

    // WebPage Schema
    const webpageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title,
      "url": `${siteUrl}${canonical}`,
      "description": description,
      "inLanguage": i18n.language === 'ar' ? 'ar' : 'en',
      "isPartOf": {
        "@type": "WebSite",
        "url": siteUrl
      }
    };

    // Professional Service Schema
    const professionalServiceSchema = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Kimberlite Commercial Brokerage",
      "url": siteUrl,
      "description": description,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Doha",
        "addressRegion": "Doha",
        "addressCountry": "QA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "25.2854",
        "longitude": "51.5310"
      },
      "priceRange": "$$",
      "areaServed": ["MENA", "Asia", "Europe", "Americas"],
      "serviceType": [
        "International Trade Facilitation",
        "Commercial Brokerage",
        "Sourcing Suppliers and Buyers",
        "Contract Negotiation",
        "Company Setup Services",
        "Banking Services Facilitation",
        "Customs Clearance",
        "Business Consulting"
      ]
    };

    // FAQPage Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What services does Kimberlite Commercial Brokerage provide?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We provide comprehensive commercial brokerage services including finding reliable suppliers and buyers, negotiating optimal commercial terms, preparing and monitoring contract implementation, providing business and market consulting, facilitating shipping and customs clearance, and assisting with company setup, banking transactions, and investor matchmaking."
          }
        },
        {
          "@type": "Question",
          "name": "Where is Kimberlite based and what regions do you serve?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We are headquartered in Doha, Qatar, and operate across the MENA region with extensive global reach. We facilitate international trade across Asia Pacific, Europe, and the Americas, connecting local demand with global supply through our extensive network."
          }
        },
        {
          "@type": "Question",
          "name": "How does Kimberlite ensure transparency in its operations?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Transparency is one of our core values. We maintain clear communication throughout every transaction, provide honest assessments of market conditions, ensure all parties understand contract terms, and conduct rigorous vetting processes for all suppliers and buyers. Our commitment to openness builds lasting trust with our clients."
          }
        },
        {
          "@type": "Question",
          "name": "Can Kimberlite help with company setup and banking in Qatar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, we provide comprehensive support for company registration and setup in Qatar, facilitate banking transactions and account opening with local financial institutions, and help connect businesses with potential investors. Our deep understanding of Qatar's business environment ensures a smooth establishment process."
          }
        },
        {
          "@type": "Question",
          "name": "What makes Kimberlite different from other commercial brokers?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our unique combination of deep MENA market expertise, extensive global network, and unwavering commitment to transparency sets us apart. We act as a true strategic partner, not just a transaction facilitator. Our Qatar base gives us unique insights into regional markets while our international connections ensure optimal commercial terms for all parties."
          }
        },
        {
          "@type": "Question",
          "name": "How long does it typically take to complete a commercial transaction?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The timeline varies depending on the complexity of the transaction, the industries involved, and regulatory requirements. Simple supplier matching can take 2-4 weeks, while comprehensive deals involving contract negotiation, customs clearance, and logistics coordination may take 2-3 months. We work efficiently to expedite processes while ensuring all due diligence is properly conducted."
          }
        }
      ]
    };

    addOrUpdateSchema('organization', organizationSchema);
    addOrUpdateSchema('website', websiteSchema);
    addOrUpdateSchema('webpage', webpageSchema);
    addOrUpdateSchema('professional-service', professionalServiceSchema);
    addOrUpdateSchema('faq', faqSchema);

  }, [title, description, keywords, canonical, ogImage, i18n.language, siteUrl]);

  return null;
}
